// Contacts tab "Manage Links" dialog — every Contact, Company, and Entity
// linked to the Property, with the same staged link / unlink / role-change
// / create flow as the Add/Edit Unit dialog (LinkedRecordsSection). Nothing
// is written until Save; the response carries the refreshed contacts
// section so the tab behind the dialog updates too.

import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";

import LinkedRecordsSection from "./LinkedRecordsSection";
import LinkDialogFooter from "./LinkDialogFooter";
import { diffLinks, linksFromRecords, settleLinks, stageAdd, stageRole, stageToggle } from "./linkStaging";
import { EMPTY_LOOKUPS, PROPERTY_LINK_KINDS, loadPropertyLinkLookups, savePropertyLinks } from "./propertyLinkLookups";
import "./LinkedRecordsDialog.css";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Typography from "@mui/material/Typography";

const PropertyLinksDialog = ({ open, property, records, employeeID, onClose, onSaved }) => {
	const { instance, accounts } = useMsal();

	const [links, setLinks] = useState(() => linksFromRecords(records));
	const [lookups, setLookups] = useState(EMPTY_LOOKUPS);
	const [saving, setSaving] = useState(false);
	const [savedFlash, setSavedFlash] = useState(false);
	const [errors, setErrors] = useState([]);

	useEffect(() => {
		if (!open) return;
		setLinks(linksFromRecords(records));
		setSavedFlash(false);
		setErrors([]);
		loadPropertyLinkLookups(instance, accounts[0])
			.then(setLookups)
			.catch((err) => setErrors([err?.response?.data?.error || "Failed to load roles from HubSpot."]));
		// Only on open — `records` refreshing after our own Save is applied below.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	const { parts, adds, removes, roleChanges } = diffLinks(links);
	const dirty = parts.length > 0;

	const updateLinks = (fn) => {
		setLinks(fn);
		setSavedFlash(false);
		setErrors([]);
	};

	const handleClose = () => {
		if (saving) return;
		onClose();
	};

	const handleSave = () => {
		if (saving || !dirty) return;
		setSaving(true);
		setErrors([]);
		savePropertyLinks(instance, accounts[0], property.id, { adds, removes, roleChanges }, employeeID)
			.then((data) => {
				const { errors: opErrors = [], ...section } = data;
				if (section.contacts) {
					setLinks(linksFromRecords(section));
					onSaved(section);
					setErrors(opErrors);
				} else {
					setLinks(settleLinks);
					setErrors([...opErrors, "Saved, but the list couldn't be reloaded — refresh the property to see the latest."]);
				}
				setSavedFlash(opErrors.length === 0 && Boolean(section.contacts));
			})
			.catch((err) => setErrors([err?.response?.data?.error || err?.message || "Failed to save."]))
			.finally(() => setSaving(false));
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ className: "linkDialogPaper" }}>
			<DialogTitle className="propertyViewerEditDialogTitle">
				Manage Links
				{property?.name && (
					<Typography variant="body2" sx={{ color: "rgba(255,255,255,0.9)", fontWeight: 400, mt: 0.75 }}>
						{property.name}
					</Typography>
				)}
			</DialogTitle>

			<DialogContent className="linkDialogContent">
				<LinkedRecordsSection
					title="Contacts, Companies & Entities"
					kinds={PROPERTY_LINK_KINDS}
					searchEndpoint="/api/jobs/property/links/search"
					links={links}
					roles={lookups.roles}
					entityTypes={lookups.entityTypes}
					companyTypes={lookups.companyTypes}
					showJobTitle
					disabled={saving}
					onAdd={(link) => updateLinks((current) => stageAdd(current, link))}
					onToggle={(key) => updateLinks((current) => stageToggle(current, key))}
					onRoleChange={(key, typeId) => updateLinks((current) => stageRole(current, key, typeId))}
				/>
			</DialogContent>

			<LinkDialogFooter
				statusText={savedFlash ? "Saved" : dirty ? `Unsaved: ${parts.join(", ")}` : ""}
				savedFlash={savedFlash}
				errors={errors}
				dirty={dirty}
				saving={saving}
				onCancel={handleClose}
				onSave={handleSave}
			/>
		</Dialog>
	);
};

export default PropertyLinksDialog;
